import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
  name: 'htmlPreview'
})
export class HtmlPreviewPipe implements PipeTransform {

  constructor(private sanitizer: DomSanitizer) {}

  /**
   * @param _previewNonce Optional; when provided (e.g. Date.now() per modal open),
   * busts Angular's pure-pipe cache so preview always re-renders the given HTML.
   */
  transform(value: string, _previewNonce?: number): SafeHtml {
    if (!value) {
      return '';
    }

    // Allow raw HTML content without sanitization to preserve scripts
    let sanitizedContent = value;

    // Scope CSS content to prevent global style leakage
    sanitizedContent = this.scopeCssContent(sanitizedContent);

    // Add CSS to make form elements look better and functional ---
    const formStyles = ``;
    sanitizedContent = formStyles + sanitizedContent;

    // Execute scripts after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.executeScripts(value);
    }, 100);

    return this.sanitizer.bypassSecurityTrustHtml(sanitizedContent);
  }

  private executeScripts(htmlContent: string): void {
    try {
      // Extract script tags from the HTML content
      const scriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi;
      let match;
      
      while ((match = scriptRegex.exec(htmlContent)) !== null) {
        const scriptContent = match[1];
        if (scriptContent.trim()) {
          // Execute the script content in global scope
          try {
            // Execute the script in the global window context
            const scriptElement = document.createElement('script');
            scriptElement.textContent = scriptContent;
            document.head.appendChild(scriptElement);
            
            // Remove the script element after execution
            setTimeout(() => {
              document.head.removeChild(scriptElement);
            }, 0);
          } catch (scriptError) {
            console.warn('Script execution error:', scriptError);
          }
        }
      }
    } catch (error) {
      console.warn('Error executing scripts:', error);
    }
  }

  // Method to scope CSS content to prevent global style leakage
  private scopeCssContent(htmlContent: string): string {
    if (!htmlContent) return '';

    // Extract CSS from <style> tags
    const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
    let scopedContent = htmlContent;

    scopedContent = scopedContent.replace(styleRegex, (match, cssContent) => {
      // If content already contains our scope class, do not re-scope to avoid double-prefixing
      if (cssContent.includes('.html-preview-isolated')) {
        return `<style>${cssContent}</style>`;
      }
      // Scope all CSS selectors to only affect the preview area
      const scopedCss = this.scopeCssSelectors(cssContent);
      return `<style>${scopedCss}</style>`;
    });

    return scopedContent;
  }

  private scopeCssSelectors(css: string): string {
    if (!css) return '';

    // Split CSS into rules
    const rules = css.split('}');
    const scopedRules: string[] = [];

    rules.forEach(rule => {
      if (rule.trim()) {
        // Split selectors and properties
        const parts = rule.split('{');
        if (parts.length === 2) {
          const selectors = parts[0].trim();
          const properties = parts[1].trim();

          if (selectors && properties) {
            // Scope each selector to only affect the preview area
            const scopedSelectors = this.scopeCssSelector(selectors);
            scopedRules.push(`${scopedSelectors} { ${properties} }`);
          }
        }
      }
    });

    return scopedRules.join('\n');
  }

  private scopeCssSelector(selector: string): string {
    // Split multiple selectors (comma-separated)
    const selectors = selector.split(',').map(s => s.trim());
    const scopedSelectors = selectors.map(sel => {
      // Skip @media, @keyframes, @import, etc.
      if (sel.startsWith('@')) {
        return sel;
      }

      // Improve scoping for root selectors like html/body
      // Replace leading html/body with the scoped container
      const rootMatch = /^(html|body)\b(.*)$/i.exec(sel);
      if (rootMatch) {
        const rest = (rootMatch[2] || '').trim();
        return rest ? `.html-preview-isolated ${rest}` : `.html-preview-isolated`;
      }

      // Scope the selector to only affect the preview area
      return `.html-preview-isolated ${sel}`;
    });

    return scopedSelectors.join(', ');
  }

  private sanitizeHtml(html: string): string {
    // Remove script tags and potentially dangerous content
    let sanitized = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+\s*=/gi, '') // Remove event handlers
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') // Remove iframe tags
      .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '') // Remove object tags
      .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, ''); // Remove embed tags

    // Clean up empty tags and excessive whitespace
    sanitized = sanitized
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/>\s+</g, '><') // Remove spaces between tags
      .trim();

    return sanitized;
  }
}
